import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';

const API_KEY = process.env.REACT_APP_API_KEY;

type Repository = {
    name: string;
    repository: string | null | undefined;
    description: string | null | undefined;
    language: string | null | undefined;
    version: string | null | undefined;
    project_version: string | null | undefined;
    frameworks: Record<string, string>;
    package_manager: string | null | undefined;
    subprojects: any[];
    test_coverage: any;
    dependencies: any[];
    infrastructure: any;
    active: boolean;
    readme: boolean;
    ignored_dirs: string[];
};

export const useSoftwarePortfolio = () => {
    const navigate = useNavigate();
    const showModal = useSignal(false);
    const modalTitle = useSignal('');
    const modalData = useSignal<any>(null);
    const modalType = useSignal<'frameworks' | 'dependencies' | 'ignored_dirs'>('frameworks');
    const expandedProjects = useSignal<Set<string>>(new Set());

    const {
        page = '0',
        pageSize = '20',
        field: fieldSorted = 'name',
        order: orderSorted = 'ASC',
        filter,
    } = useParams();

    let searchTerm = '';
    if (filter) {
        [searchTerm = ''] = filter.match(/=(.*?);/g)?.map((m: string) => m.slice(1, -1)) ?? [''];
    }

    const url = `https://7u01x4ndpl.execute-api.us-east-1.amazonaws.com/v1/softwares/page-list?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(searchTerm)}`;

    const getCoveragePercentage = (testCoverage: any): number => {
        if (!testCoverage || !testCoverage.line_rate) return 0;
        const rate = parseFloat(testCoverage.line_rate);
        return Math.round(rate * 100);
    };

    const getGitHubUrl = (repository: string | null | undefined): string | null => {
        if (!repository) return null;

        // Converter git@github.com:user/repo.git para https://github.com/user/repo
        if (repository.startsWith('git@github.com:')) {
            return repository
                .replace('git@github.com:', 'https://github.com/')
                .replace('.git', '');
        }

        // Se já é HTTPS, apenas remover .git se existir
        if (repository.startsWith('https://github.com/')) {
            return repository.replace('.git', '');
        }

        return repository;
    };

    const toggleProject = (projectName: string) => {
        const newExpanded = new Set(expandedProjects.value);
        if (newExpanded.has(projectName)) {
            newExpanded.delete(projectName);
        } else {
            newExpanded.add(projectName);
        }
        expandedProjects.value = newExpanded;
    };

    const openModal = (title: string, data: any, type: 'frameworks' | 'dependencies' | 'ignored_dirs') => {
        modalTitle.value = title;
        modalData.value = data;
        modalType.value = type;
        showModal.value = true;
    };

    const pageChanged = (pageNumber: number) => {
        pageNumber = pageNumber - 1;
        navigate(urlNavigate(pageNumber, Number(pageSize), fieldSorted, orderSorted, searchTerm), { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(urlNavigate(0, size, fieldSorted, orderSorted, searchTerm), { replace: true });
    };

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(0, Number(pageSize), field, order, searchTerm), { replace: true });
    };

    const searchChanged = (value: string) => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, value), { replace: true });
    };

    const onFilterClean = () => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, ''), { replace: true });
    };

    const urlNavigate = (pageNumber: number, size: number, field: string, order: string, search: string) => {
        return `/ti/software-portfolio/${pageNumber}/${size}/${field}/${order}/d=${search};`;
    };

    return {
        // State
        showModal,
        modalTitle,
        modalData,
        modalType,
        expandedProjects,
        
        // Params
        page,
        pageSize,
        fieldSorted,
        orderSorted,
        searchTerm,
        url,
        API_KEY,
        
        // Functions
        getCoveragePercentage,
        getGitHubUrl,
        toggleProject,
        openModal,
        pageChanged,
        pageSizeChanged,
        pageSorted,
        searchChanged,
        onFilterClean,
        
        // Types
        Repository: {} as Repository
    };
};
